import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TopNav from '../components/TopNav';
import ComposeModal from '../components/ComposeModal';
import Footer from '../components/Footer';
import { postsAPI, platformsAPI, inboxAPI, settingsAPI } from '../api';
import { DERIVED_PLATFORMS } from '../utils/platformUtils';
import './Settings.css';

// Platform display info
const PLATFORM_INFO = {
  reddit: {
    name: 'Reddit',
    color: '#FF4500',
    icon: '🔴',
    description: 'Post to subreddits, manage comments and messages'
  },
  tiktok: {
    name: 'TikTok',
    color: '#000000',
    icon: '🎵',
    description: 'Share videos and manage comments'
  },
  twitter: {
    name: 'Twitter/X',
    color: '#1DA1F2',
    icon: '🐦',
    description: 'Tweet, reply, and manage DMs'
  },
  instagram: {
    name: 'Instagram',
    color: '#E4405F',
    icon: '📷',
    description: 'Share posts and manage comments (via Instagram Login)'
  },
  instagram_via_facebook: {
    name: 'Instagram via Facebook',
    color: '#E4405F',
    icon: '📷',
    description: 'Share posts and manage comments (via Facebook Login for Business)'
  },
  threads: {
    name: 'Threads',
    color: '#000000',
    icon: '#',
    description: 'Publish Threads posts, replies, mentions, and insights'
  },
  linkedin: {
    name: 'LinkedIn',
    color: '#0A66C2',
    icon: '💼',
    description: 'Share professional updates'
  },
  youtube: {
    name: 'YouTube',
    color: '#FF0000',
    icon: '▶️',
    description: 'Manage comments on your YouTube videos'
  }
};

const Settings = () => {
  const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [subscribingWebhooks, setSubscribingWebhooks] = useState(null);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('accounts');

  // Sync Settings state
  const [syncPolicies, setSyncPolicies] = useState([]);
  const [syncStatuses, setSyncStatuses] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [webhookAlerts, setWebhookAlerts] = useState([]);
  const [syncing, setSyncing] = useState({});
  const [editingPolicy, setEditingPolicy] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const loadConnections = useCallback(async () => {
    try {
      const data = await platformsAPI.getConnections();
      setConnections(data);
      return data;
    } catch (error) {
      console.error('Error loading connections:', error);
      return [];
    }
  }, []);

  // Handle OAuth callback messages
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get('success');
    const error = params.get('error');
    const platform = params.get('platform');
    const message = params.get('message');

    if (success === 'true' && platform) {
      setNotification({
        type: 'success',
        message: `Successfully connected ${PLATFORM_INFO[platform]?.name || platform}!`
      });
      // Clear URL params
      navigate('/settings', { replace: true });
      // Refresh connections locally and notify SideIcons with the full connection
      // data so SideIcons does NOT need to make another API call.
      loadConnections().then((data) => {
        // Notify for the callback platform AND anything derived from it (e.g.
        // the facebook Page connection created by instagram_via_facebook);
        // otherwise the derived icon only appears after a full page reload.
        const affected = [platform, ...(DERIVED_PLATFORMS[platform] || [])];
        affected.forEach((affectedPlatform) => {
          const connection = data.find((c) => c.platform === affectedPlatform);
          if (connection) {
            window.dispatchEvent(new CustomEvent('connection-added', {
              detail: { platform: affectedPlatform, connection },
            }));
          }
        });
      });
    } else if (error && platform) {
      let errorMessage = `Failed to connect ${PLATFORM_INFO[platform]?.name || platform}`;
      if (error === 'invalid_state') {
        errorMessage += ': Invalid or expired session. Please try again.';
      } else if (error === 'token_exchange_failed') {
        errorMessage += `: ${message || 'Token exchange failed'}`;
      } else {
        errorMessage += `: ${error}`;
      }
      setNotification({
        type: 'error',
        message: errorMessage
      });
      navigate('/settings', { replace: true });
    }
  }, [location, navigate, loadConnections]);

  // Auto-dismiss notifications
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loadAvailablePlatforms = useCallback(async () => {
    try {
      const data = await platformsAPI.getAvailable();
      setAvailablePlatforms(data);
    } catch (error) {
      console.error('Error loading platforms:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadConnections(), loadAvailablePlatforms()]);
      setLoading(false);
    };
    loadData();
  }, [loadConnections, loadAvailablePlatforms]);

  const handleConnect = async (platform) => {
    setConnecting(platform);
    try {
      const data = await platformsAPI.initiateOAuth(platform);
      // Redirect to OAuth URL
      window.location.href = data.oauth_url;
    } catch (error) {
      console.error('Error initiating OAuth:', error);
      setNotification({
        type: 'error',
        message: `Failed to connect ${PLATFORM_INFO[platform]?.name || platform}: ${error.message}`
      });
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform, connectionId) => {
    if (!window.confirm(`Are you sure you want to disconnect ${PLATFORM_INFO[platform]?.name || platform}?`)) {
      return;
    }

    try {
      const result = await platformsAPI.disconnect(platform, connectionId);
      setNotification({
        type: 'success',
        message: `Disconnected ${PLATFORM_INFO[platform]?.name || platform}`
      });
      loadConnections();
      // Retire derived connections in the UI too (e.g. facebook derived from
      // instagram_via_facebook), matching the backend cascade.
      [platform, ...(result?.also_disconnected || [])].forEach((removedPlatform) => {
        window.dispatchEvent(new CustomEvent('connection-removed', {
          detail: { platform: removedPlatform },
        }));
      });
    } catch (error) {
      console.error('Error disconnecting:', error);
      setNotification({
        type: 'error',
        message: `Failed to disconnect: ${error.message}`
      });
    }
  };

  const handleSubscribeFacebookWebhooks = async (connectionId) => {
    setSubscribingWebhooks(connectionId);
    try {
      const data = await platformsAPI.subscribeFacebookWebhooks(connectionId);
      setNotification({
        type: 'success',
        message: `Facebook webhooks subscribed for Page ${data.page_id}.`
      });
      loadConnections();
    } catch (error) {
      console.error('Error subscribing Facebook webhooks:', error);
      setNotification({
        type: 'error',
        message: `Failed to subscribe Facebook webhooks: ${error.response?.data?.message || error.message}`
      });
    } finally {
      setSubscribingWebhooks(null);
    }
  };

  const handleComposeClick = () => {
    setIsComposeModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsComposeModalOpen(false);
  };

  const handleSubmitPost = async (postData, postId = null) => {
    try {
      if (postId) {
        await postsAPI.update(postId, postData);
      } else {
        await postsAPI.create(postData);
      }
    } catch (error) {
      console.error('Error saving post:', error);
      throw error;
    }
  };

  // ---- Sync Settings data loading ----
  const loadSyncSettings = useCallback(async () => {
    try {
      const [policiesRes, statusRes, usageRes, historyRes, alertsRes] = await Promise.all([
        settingsAPI.getSyncPolicies().catch(() => ({ policies: [] })),
        settingsAPI.getSyncStatus().catch(() => ({ status: [] })),
        settingsAPI.getUsageSummary().catch(() => ({})),
        settingsAPI.getUsageHistory().catch(() => ({ operations: [] })),
        settingsAPI.getWebhookAlerts().catch(() => ({ alerts: [] })),
      ]);
      setSyncPolicies(policiesRes.policies || []);
      setSyncStatuses(statusRes.status || []);
      setUsageSummary(usageRes);
      setUsageHistory(historyRes.operations || []);
      setWebhookAlerts(alertsRes.alerts || []);
    } catch (error) {
      console.error('Error loading sync settings:', error);
    }
  }, []);

  // Load sync settings when tab changes
  useEffect(() => {
    if (activeTab === 'sync') {
      loadSyncSettings();
    }
  }, [activeTab, loadSyncSettings]);

  // ---- Sync handlers ----
  const handleSyncNow = async (connectionId, mode) => {
    setSyncing(prev => ({ ...prev, [connectionId]: mode }));
    try {
      await inboxAPI.sync({ connection_id: connectionId, mode });
      setNotification({
        type: 'success',
        message: `Sync (${mode}) completed.`,
      });
      loadSyncSettings();
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Sync failed: ${error.message}`,
      });
    } finally {
      setSyncing(prev => ({ ...prev, [connectionId]: null }));
    }
  };

  const handleSavePolicy = async (connectionId, policyData) => {
    try {
      await settingsAPI.updateSyncPolicy(connectionId, policyData);
      setNotification({
        type: 'success',
        message: 'Sync policy updated.',
      });
      setEditingPolicy(null);
      loadSyncSettings();
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Failed to update policy: ${error.message}`,
      });
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await settingsAPI.acknowledgeWebhookAlert(alertId);
      setWebhookAlerts(prev => prev.filter(a => a.id !== alertId));
      setNotification({ type: 'success', message: 'Alert acknowledged.' });
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Failed to acknowledge alert: ${error.message}`,
      });
    }
  };

  // Get connected platform names
  const connectedPlatforms = connections.map(c => c.platform);
  // Hide facebook connector from settings (no separate OAuth — created automatically when user connects via instagram_via_facebook)
  // Hide regular instagram when instagram_via_facebook is connected (recommended implementation — avoids duplicate/conflicting connections)
  const hasInstagramViaFacebook = connectedPlatforms.includes('instagram_via_facebook');
  const connectedAccounts = connections.filter(c =>
    c.platform !== 'facebook' &&
    !(c.platform === 'instagram' && hasInstagramViaFacebook)
  );
  const availableToConnect = availablePlatforms.filter(
    p =>
      p.configured &&
      !connectedPlatforms.includes(p.name) &&
      p.name !== 'facebook' &&
      !(p.name === 'instagram' && hasInstagramViaFacebook)
  );
  const unconfiguredPlatforms = availablePlatforms.filter(
    p =>
      !p.configured &&
      p.name !== 'facebook' &&
      !(p.name === 'instagram' && hasInstagramViaFacebook)
  );

  return (
    <div className="settings-page">
      <TopNav activePage="settings" onComposeClick={handleComposeClick} />

      <main className="settings-container">
        <h1>Settings</h1>
        <p className="subtitle">Manage your account and connected platforms</p>

        {/* Notification */}
        {notification && (
          <div className={`notification ${notification.type}`}>
            {notification.message}
            <button className="close-btn" onClick={() => setNotification(null)}>×</button>
          </div>
        )}

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'accounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('accounts')}
          >
            Connected Accounts
          </button>
          <button
            className={`settings-tab ${activeTab === 'sync' ? 'active' : ''}`}
            onClick={() => setActiveTab('sync')}
          >
            Sync Settings
          </button>
        </div>

        {/* Connected Accounts Tab */}
        {activeTab === 'accounts' && (
          <>
            <section className="settings-section">
              <h2>Connected Accounts</h2>
              <p className="section-description">
                Manage your connected social media accounts
              </p>

              {loading ? (
                <div className="loading-state">Loading...</div>
              ) : connectedAccounts.length === 0 ? (
                <div className="empty-state">
                  <p>No accounts connected yet. Connect a platform below to get started.</p>
                </div>
              ) : (
                <div className="connections-list">
                  {connectedAccounts.map((connection) => {
                    const platformInfo = PLATFORM_INFO[connection.platform] || {};
                    return (
                      <div key={connection.id} className="connection-card">
                        <div 
                          className="platform-icon" 
                          style={{ backgroundColor: platformInfo.color }}
                        >
                          {platformInfo.icon || connection.platform[0].toUpperCase()}
                        </div>
                        <div className="connection-info">
                          <div className="platform-name">{platformInfo.name || connection.platform}</div>
                          <div className="account-details">
                            {connection.platform_avatar_url && (
                              <img 
                                src={connection.platform_avatar_url} 
                                alt="" 
                                className="account-avatar"
                              />
                            )}
                            <span className="account-username">
                              @{connection.platform_username || connection.platform_display_name}
                            </span>
                          </div>
                          <div className="connection-meta">
                            Connected {new Date(connection.connected_at).toLocaleDateString()}
                          </div>
                          {connection.links && (
                            <div className="connection-links">
                              {connection.links.facebook_page_name && (
                                <span>Facebook Page: {connection.links.facebook_page_name}</span>
                              )}
                              {connection.links.instagram_business_account_id && (
                                <span>IG account ID: {connection.links.instagram_business_account_id}</span>
                              )}
                            </div>
                          )}
                          {connection.platform === 'instagram_via_facebook' && !connection.capabilities?.can_fetch_posts && (
                            <div className="connection-warning" role="alert">
                              No Instagram professional account is linked to this Facebook Page.
                              Link one in Meta Business Suite, then reconnect and pick that Page.
                            </div>
                          )}
                        </div>
                        <div className="connection-actions">
                          <button 
                            className="disconnect-btn"
                            onClick={() => handleDisconnect(connection.platform, connection.id)}
                          >
                            Disconnect
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="settings-section">
              <h2>Connect New Account</h2>
              <p className="section-description">
                Add more social media accounts to manage from Trckle
              </p>

              {availableToConnect.length === 0 && !loading ? (
                <div className="empty-state">
                  <p>All available platforms are already connected.</p>
                </div>
              ) : (
                <div className="platforms-grid">
                  {availableToConnect.map((platform) => {
                    const platformInfo = PLATFORM_INFO[platform.name] || {};
                    const isConnecting = connecting === platform.name;
                    
                    return (
                      <div key={platform.name} className="platform-card">
                        <div 
                          className="platform-icon-large"
                          style={{ backgroundColor: platformInfo.color || platform.color }}
                        >
                          {platformInfo.icon || platform.name[0].toUpperCase()}
                        </div>
                        <div className="platform-info">
                          <div className="platform-name">{platformInfo.name || platform.display_name}</div>
                          <div className="platform-description">
                            {platformInfo.description || `Connect your ${platform.display_name} account`}
                          </div>
                        </div>
                        <button
                          className="connect-btn"
                          onClick={() => handleConnect(platform.name)}
                          disabled={isConnecting}
                        >
                          {isConnecting ? 'Connecting...' : 'Connect'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {unconfiguredPlatforms.length > 0 && (
                <div className="coming-soon">
                  <h3>Coming Soon</h3>
                  <div className="coming-soon-list">
                    {unconfiguredPlatforms.map((platform) => {
                      const platformInfo = PLATFORM_INFO[platform.name] || {};
                      return (
                        <div key={platform.name} className="coming-soon-item">
                          <span className="platform-icon-small">
                            {platformInfo.icon || platform.name[0].toUpperCase()}
                          </span>
                          <span>{platformInfo.name || platform.display_name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          </>
        )}

        {/* Sync Settings Tab */}
        {activeTab === 'sync' && (
          <>
            {/* Per-connection sync cards */}
            <section className="settings-section">
              <h2>Sync Controls</h2>
              <p className="section-description">
                Manage sync behavior and trigger manual syncs per connection
              </p>

              {syncStatuses.length === 0 ? (
                <div className="empty-state">
                  <p>No connections to sync.</p>
                </div>
              ) : (
                <div className="connections-list">
                  {syncStatuses.map((status) => {
                    const platformInfo = PLATFORM_INFO[status.platform] || {};
                    const policy = syncPolicies.find(p => p.connection_id === status.connection_id)
                      || syncPolicies.find(p => p.platform === status.platform && !p.connection_id)
                      || {};
                    const isSyncing = syncing[status.connection_id];
                    const editMode = editingPolicy === status.connection_id;

                    return (
                      <div key={status.connection_id} className="sync-connection-card">
                        <div 
                          className="platform-icon" 
                          style={{ backgroundColor: platformInfo.color }}
                        >
                          {platformInfo.icon || status.platform[0].toUpperCase()}
                        </div>
                        <div className="sync-connection-info">
                          <div className="platform-name">{platformInfo.name || status.platform}</div>
                          <div className="sync-meta">
                            <span>Last sync: {status.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : 'Never'}</span>
                            <span>Scopes: {status.scope_count}</span>
                            {status.last_operation && (
                              <span className={`sync-status-badge ${status.last_operation.status}`}>
                                {status.last_operation.status}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="sync-actions">
                          <select
                            className="sync-mode-select"
                            data-conn-id={status.connection_id}
                            value={isSyncing || 'incremental'}
                            disabled={!!isSyncing}
                            onChange={(e) => {}}
                          >
                            <option value="incremental">Incremental</option>
                            <option value="backfill">Backfill</option>
                            <option value="recheck">Recheck</option>
                            <option value="reset">Reset</option>
                          </select>
                          <button
                            className="sync-now-btn"
                            disabled={!!isSyncing}
                            onClick={() => {
                              const mode = isSyncing || 'incremental';
                              // Find the select value properly
                              const select = document.querySelector(`[data-conn-id="${status.connection_id}"]`);
                              const selectedMode = select ? select.value : mode;
                              handleSyncNow(status.connection_id, selectedMode);
                            }}
                          >
                            {isSyncing ? `Syncing (${isSyncing})...` : 'Sync Now'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Policy editing per connection */}
            <section className="settings-section">
              <h2>Sync Policy</h2>
              <p className="section-description">
                Configure budget limits and retry behavior per connection
              </p>

              {syncPolicies.filter(p => p.connection_id).length === 0 && syncStatuses.length > 0 ? (
                <div className="empty-state">
                  <p>Using platform defaults. Click "Edit" below to customize.</p>
                </div>
              ) : (
                <div className="connections-list">
                  {syncStatuses.map((status) => {
                    const policy = syncPolicies.find(p => p.connection_id === status.connection_id)
                      || syncPolicies.find(p => p.platform === status.platform && !p.connection_id)
                      || {};
                    const editMode = editingPolicy === status.connection_id;

                    return (
                      <div key={`policy-${status.connection_id}`} className="policy-card">
                        <div className="policy-header">
                          <span className="platform-name">{status.platform}</span>
                          <button
                            className="metadata-btn"
                            onClick={() => setEditingPolicy(
                              editMode ? null : status.connection_id
                            )}
                          >
                            {editMode ? 'Cancel' : 'Edit'}
                          </button>
                        </div>
                        {editMode ? (
                          <div className="policy-form" data-policy-conn={status.connection_id}>
                            <div className="policy-row">
                              <label>Max pages per run</label>
                              <input type="number" defaultValue={policy.max_feed_pages_per_run || 100} />
                              <label>Max run (seconds)</label>
                              <input type="number" defaultValue={policy.max_run_seconds || 300} />
                            </div>
                            <div className="policy-row">
                              <label>Max root comment pages/post</label>
                              <input type="number" defaultValue={policy.max_root_comment_pages_per_post || 20} />
                              <label>Max root comments/post</label>
                              <input type="number" defaultValue={policy.max_root_comments_per_post || 1000} />
                            </div>
                            <div className="policy-row">
                              <label>Max provider requests/run</label>
                              <input type="number" defaultValue={policy.max_provider_requests_per_run || 1000} />
                              <label>Retry max attempts</label>
                              <input type="number" defaultValue={policy.retry_max_attempts || 3} />
                            </div>
                            <button
                              className="sync-now-btn"
                              onClick={() => {
                                const inputs = document.querySelectorAll(`[data-policy-conn="${status.connection_id}"] input`);
                                const vals = {};
                                const labels = [
                                  'max_feed_pages_per_run', 'max_run_seconds',
                                  'max_root_comment_pages_per_post', 'max_root_comments_per_post',
                                  'max_provider_requests_per_run', 'retry_max_attempts',
                                ];
                                inputs.forEach((input, i) => {
                                  if (labels[i]) vals[labels[i]] = parseInt(input.value) || 0;
                                });
                                handleSavePolicy(status.connection_id, vals);
                              }}
                            >
                              Save Policy
                            </button>
                          </div>
                        ) : (
                          <div className="policy-values">
                            <span>Pages: {policy.max_feed_pages_per_run || 100}</span>
                            <span>Time: {policy.max_run_seconds || 300}s</span>
                            <span>Comments/pages: {policy.max_root_comment_pages_per_post || 20}</span>
                            <span>Retries: {policy.retry_max_attempts || 3}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Usage Summary */}
            <section className="settings-section">
              <h2>Usage Summary</h2>
              <p className="section-description">
                Last 30 days of sync activity
              </p>

              {usageSummary ? (
                <div className="usage-summary">
                  <div className="usage-stat">
                    <div className="usage-value">{usageSummary.operations_count}</div>
                    <div className="usage-label">Operations</div>
                  </div>
                  <div className="usage-stat">
                    <div className="usage-value">{usageSummary.succeeded}</div>
                    <div className="usage-label">Succeeded</div>
                  </div>
                  <div className="usage-stat">
                    <div className="usage-value">{usageSummary.partial}</div>
                    <div className="usage-label">Partial</div>
                  </div>
                  <div className="usage-stat">
                    <div className="usage-value">{usageSummary.failed}</div>
                    <div className="usage-label">Failed</div>
                  </div>
                  <div className="usage-stat">
                    <div className="usage-value">{usageSummary.total_metered_units}</div>
                    <div className="usage-label">Metered Units</div>
                  </div>
                  <div className="usage-stat">
                    <div className="usage-value">{usageSummary.total_provider_requests}</div>
                    <div className="usage-label">Provider Requests</div>
                  </div>
                </div>
              ) : (
                <div className="empty-state"><p>No usage data available.</p></div>
              )}

              {/* Recent operations */}
              {usageHistory.length > 0 && (
                <div className="usage-history">
                  <h3>Recent Operations</h3>
                  <table className="usage-table">
                    <thead>
                      <tr>
                        <th>Started</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Requests</th>
                        <th>Items</th>
                        <th>Units</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageHistory.slice(0, 20).map((op, i) => (
                        <tr key={i}>
                          <td>{op.started_at ? new Date(op.started_at).toLocaleString() : '-'}</td>
                          <td>{op.operation_type}</td>
                          <td><span className={`sync-status-badge ${op.status}`}>{op.status}</span></td>
                          <td>{op.provider_requests || 0}</td>
                          <td>{op.items_upserted || 0}</td>
                          <td>{op.metered_units || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Webhook Alerts (D5): provider events we couldn't resolve to an
                active connection. Surfaced so a rotated subscription id or a
                disconnected account is visible instead of silently dropped. */}
            <section className="settings-section">
              <h2>Webhook Alerts</h2>
              <p className="section-description">
                Incoming webhook events that could not be matched to an active connection.
              </p>

              {webhookAlerts.length === 0 ? (
                <div className="empty-state">
                  <p>No open webhook alerts.</p>
                </div>
              ) : (
                <div className="connections-list">
                  {webhookAlerts.map((alert) => {
                    const platformInfo = PLATFORM_INFO[alert.platform] || {};
                    return (
                      <div key={alert.id} className="webhook-alert-card">
                        <div
                          className="platform-icon"
                          style={{ backgroundColor: platformInfo.color }}
                        >
                          {platformInfo.icon || (alert.platform || '?')[0].toUpperCase()}
                        </div>
                        <div className="sync-connection-info">
                          <div className="platform-name">
                            {platformInfo.name || alert.platform} — {alert.reason}
                          </div>
                          <div className="sync-meta">
                            <span>Entity: {alert.entity_id || 'unknown'}</span>
                            <span>Seen {alert.occurrences}×</span>
                            <span>
                              Last: {alert.last_seen_at ? new Date(alert.last_seen_at).toLocaleString() : '-'}
                            </span>
                          </div>
                          {alert.detail && (
                            <div className="connection-meta">{alert.detail}</div>
                          )}
                        </div>
                        <div className="connection-actions">
                          <button
                            className="sync-now-btn"
                            onClick={() => handleAcknowledgeAlert(alert.id)}
                          >
                            Acknowledge
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </main>

      <ComposeModal 
        isOpen={isComposeModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPost}
      />
      <Footer />
    </div>
  );
};

export default Settings;
